import { useQuery } from '@tanstack/react-query';
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import {
  queryGlobalSearch,
  type GlobalSearchResponse,
  type GlobalSearchResult,
} from '@/features/search/data';
import {
  appearanceFeatureFlags,
  isFeatureFlagEnabled,
} from '@/shared/featureFlags/appearanceFeatureFlags';
import { queryKeys } from '@/shared/hooks';
import { isLightTheme } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { Box, CircularProgress, IconButton, Popover, SearchIcon, Stack, Text } from '@/ui';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import type { ApiError } from '@/shared/data/client';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';

type GlobalSearchProps = {
  userId: string | null;
  isSuperuser: boolean;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
};

type NavigationResultDefinition = {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  aliases: string[];
  superuserOnly?: boolean;
};

const navigationResults: NavigationResultDefinition[] = [
  {
    id: 'account-settings',
    title: 'Account Settings',
    subtitle: 'Settings',
    href: '/account-settings',
    aliases: ['account', 'profile', 'settings'],
  },
  {
    id: 'workspace-settings',
    title: 'Workspace Settings',
    subtitle: 'Settings',
    href: '/workspace-settings',
    aliases: ['workspace', 'organisation', 'organization', 'team', 'users', 'settings'],
  },
  {
    id: 'project-settings',
    title: 'Project Settings',
    subtitle: 'Settings',
    href: '/project-settings',
    aliases: ['project', 'settings'],
  },
  {
    id: 'plan-settings',
    title: 'Plan Settings',
    subtitle: 'Settings',
    href: '/plan-settings',
    aliases: ['plan', 'billing', 'subscription', 'settings'],
  },
  {
    id: 'superuser',
    title: 'Superuser',
    subtitle: 'Admin',
    href: '/superuser',
    aliases: ['admin', 'superuser'],
    superuserOnly: true,
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    subtitle: 'Admin',
    href: '/superuser?tab=feature-flags',
    aliases: ['flags', 'feature flags', 'admin'],
    superuserOnly: true,
  },
];

const resultGroupLabels: Readonly<Record<GlobalSearchResult['source'], string>> = {
  entity: 'Results',
  navigation: 'Navigation',
};

const toolbarSearchWidth = 220;
const toolbarSearchWidthPx = `${toolbarSearchWidth}px`;
const toolbarSearchHeight = 32;
const toolbarSearchIconSize = 18;
const toolbarSearchButtonIconSize = 24;
const minSearchLength = 2;
const debounceMs = 200;

const buildNavigationResult = (definition: NavigationResultDefinition): GlobalSearchResult => ({
  id: `navigation:${definition.id}`,
  source: 'navigation',
  type: 'navigation',
  title: definition.title,
  subtitle: definition.subtitle,
  href: definition.href,
  context: { organisationId: null, projectId: null },
});

const matchNavigationResults = (query: string, isSuperuser: boolean): GlobalSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) {
    return [];
  }
  return navigationResults
    .filter((definition) => !definition.superuserOnly || isSuperuser)
    .filter((definition) => {
      const values = [definition.title, definition.subtitle, ...definition.aliases];
      return values.some((value) => value.toLowerCase().includes(normalizedQuery));
    })
    .map(buildNavigationResult);
};

const groupResults = (
  results: GlobalSearchResult[],
): Array<{ source: GlobalSearchResult['source']; results: GlobalSearchResult[] }> => {
  const sources: Array<GlobalSearchResult['source']> = ['navigation', 'entity'];
  return sources.flatMap((source) => {
    const sourceResults = results.filter((result) => result.source === source);
    return sourceResults.length > 0 ? [{ source, results: sourceResults }] : [];
  });
};

export const GlobalSearch = ({
  userId,
  isSuperuser,
  featureFlags,
}: GlobalSearchProps): ReactElement | null => {
  const layoutMode = useLayoutMode();
  const navigate = useNavigate();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);
  const [desktopSearchExpanded, setDesktopSearchExpanded] = useState(false);
  const [desktopSearchRendered, setDesktopSearchRendered] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const globalSearchEnabled = isFeatureFlagEnabled(
    featureFlags,
    appearanceFeatureFlags.globalSearch,
  );
  const isCompact = layoutMode !== layoutModeTokens.wide;
  const trimmedSearch = searchValue.trim();
  const debouncedTrimmedSearch = debouncedSearchValue.trim();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, debounceMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  const entityQuery = useQuery<GlobalSearchResponse, ApiError>({
    queryKey: queryKeys.globalSearch(
      debouncedTrimmedSearch.length >= minSearchLength ? debouncedTrimmedSearch : null,
      selectedOrganisationId,
      selectedProjectId,
      userId,
    ),
    enabled:
      globalSearchEnabled && userId !== null && debouncedTrimmedSearch.length >= minSearchLength,
    queryFn: async () =>
      await queryGlobalSearch({
        query: debouncedTrimmedSearch,
        organisationId: selectedOrganisationId ?? null,
        projectId: selectedProjectId ?? null,
      }),
    staleTime: 10_000,
  });

  const navigationMatches = useMemo(
    () => matchNavigationResults(trimmedSearch, isSuperuser),
    [isSuperuser, trimmedSearch],
  );
  const entityResults = entityQuery.data?.results ?? [];
  const visibleResults = useMemo(
    () => [...navigationMatches, ...entityResults],
    [entityResults, navigationMatches],
  );
  const resultGroups = useMemo(() => groupResults(visibleResults), [visibleResults]);
  const open = anchorElement !== null;

  useEffect(() => {
    setActiveIndex(0);
  }, [trimmedSearch, visibleResults.length]);

  useEffect(() => {
    if (isCompact) {
      setDesktopSearchRendered(false);
      return;
    }
    if (desktopSearchExpanded) {
      setDesktopSearchRendered(true);
      return;
    }
    setDesktopSearchRendered(false);
  }, [desktopSearchExpanded, isCompact]);

  useEffect(() => {
    if (isCompact || !desktopSearchExpanded || !desktopSearchRendered) return;

    const animationFrame = window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input === null) return;

      input.focus();
      const caretPosition = input.value.length;
      input.setSelectionRange(caretPosition, caretPosition);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [desktopSearchExpanded, desktopSearchRendered, isCompact]);

  useEffect(() => {
    if (typeof document === 'undefined' || isCompact) return;

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopSearchRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('#global-search-results')) return;
      setDesktopSearchExpanded(false);
      setSearchFocused(false);
      closeSearch();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isCompact]);

  if (!globalSearchEnabled || userId === null) {
    return null;
  }

  const closeSearch = (): void => {
    setAnchorElement(null);
    setActiveIndex(0);
  };

  const openSearch = (element: HTMLElement): void => {
    setAnchorElement(element);
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const openDesktopSearchForValue = (element: HTMLElement, value: string): void => {
    if (value.trim().length === 0) {
      closeSearch();
      return;
    }
    openSearch(element);
  };

  const handleSelectResult = (result: GlobalSearchResult): void => {
    if (result.source === 'entity') {
      if (result.context.organisationId !== selectedOrganisationId) {
        setSelectedOrganisationId(result.context.organisationId);
      }
      if (result.context.projectId !== selectedProjectId) {
        setSelectedProjectId(result.context.projectId);
      }
    }
    closeSearch();
    void navigate(result.href);
    setDesktopSearchExpanded(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value;
    setSearchValue(nextValue);
    if (!open) {
      openDesktopSearchForValue(event.currentTarget, nextValue);
    } else if (!isCompact && nextValue.trim().length === 0) {
      closeSearch();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(visibleResults.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      setDesktopSearchExpanded(false);
      return;
    }
    if (event.key === 'Enter') {
      if (visibleResults.length === 0) {
        return;
      }
      const result = visibleResults[activeIndex];
      event.preventDefault();
      handleSelectResult(result);
    }
  };

  const showLoading = entityQuery.isFetching && debouncedTrimmedSearch.length >= minSearchLength;
  const showEmpty =
    trimmedSearch.length >= minSearchLength &&
    !showLoading &&
    !entityQuery.isError &&
    visibleResults.length === 0;

  const searchField = (
    <SearchFilterField
      value={searchValue}
      placeholder="Search"
      onChange={handleInputChange}
      inputAriaLabel="Global Search"
      inputBackgroundColor="background.paper"
      controlHeight={toolbarSearchHeight}
      compact
      focused={!isCompact && searchFocused}
      leadingSearchAdornment
      trailingSearchAdornment={false}
      filledClearIcon={false}
      slotProps={{
        htmlInput: {
          ref: (instance: HTMLInputElement | null) => {
            inputRef.current = instance;
          },
          style: {
            fontSize: '14px',
            lineHeight: '20px',
          },
          role: 'combobox',
          'aria-expanded': open,
          'aria-controls': open ? 'global-search-results' : undefined,
          onFocus: (event: FocusEvent<HTMLInputElement>) => {
            setSearchFocused(true);
            if (isCompact) {
              openSearch(event.currentTarget);
              return;
            }
            openDesktopSearchForValue(event.currentTarget, searchValue);
          },
          onBlur: () => {
            setSearchFocused(false);
          },
          onKeyDown: handleKeyDown,
        },
      }}
      sx={{
        width: isCompact ? 'min(78vw, 360px)' : toolbarSearchWidthPx,
        minWidth: isCompact ? 'min(78vw, 360px)' : toolbarSearchWidthPx,
        maxWidth: isCompact ? 'min(78vw, 360px)' : toolbarSearchWidthPx,
        '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
          height: `${toolbarSearchHeight}px !important`,
          borderRadius: s('sm'),
          backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
          color: (theme) =>
            isLightTheme(theme) ? 'rgba(31, 45, 65, 0.78)' : 'rgba(244, 247, 251, 0.84)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme) => `${theme.app.colors.border} !important`,
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme) => `${theme.app.colors.divider} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme) => `${theme.app.colors.primary} !important`,
          borderWidth: '1.5px !important',
        },
        '&& .MuiInputBase-input, && .MuiOutlinedInput-input, && input': {
          height: `${toolbarSearchHeight}px !important`,
          paddingLeft: `${s('sm')} !important`,
          paddingInlineStart: `${s('sm')} !important`,
          paddingRight: `${s('sm')} !important`,
          paddingInlineEnd: `${s('sm')} !important`,
          color: (theme) =>
            `${
              isLightTheme(theme) ? 'rgba(31, 45, 65, 0.78)' : 'rgba(244, 247, 251, 0.84)'
            } !important`,
          fontSize: '14px !important',
          lineHeight: '20px !important',
        },
        '&& .MuiInputBase-input::placeholder, && .MuiOutlinedInput-input::placeholder, && input::placeholder':
          {
            color: (theme) =>
              `${
                isLightTheme(theme) ? 'rgba(31, 45, 65, 0.56)' : 'rgba(244, 247, 251, 0.54)'
              } !important`,
            fontSize: '14px !important',
            lineHeight: '20px !important',
            opacity: 0.82,
          },
        '& .MuiInputAdornment-root, & .MuiSvgIcon-root': {
          color: (theme) =>
            `${
              isLightTheme(theme) ? 'rgba(31, 45, 65, 0.62)' : 'rgba(244, 247, 251, 0.72)'
            } !important`,
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          width: `${toolbarSearchIconSize}px`,
          height: `${toolbarSearchIconSize}px`,
          stroke: 'currentColor',
          strokeWidth: 0.08,
          transform: 'translateY(0)',
        },
      }}
    />
  );
  const desktopSearchField = (
    <Box
      ref={desktopSearchRef}
      sx={{
        width: toolbarSearchHeight,
        height: toolbarSearchHeight,
        minHeight: toolbarSearchHeight,
        minWidth: toolbarSearchHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        position: 'relative',
        flexShrink: 0,
        overflow: 'visible',
      }}
    >
      {desktopSearchRendered ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: toolbarSearchWidth,
            height: toolbarSearchHeight,
            display: 'flex',
            justifyContent: 'flex-end',
            clipPath: desktopSearchExpanded
              ? 'inset(0 0 0 0)'
              : `inset(0 0 0 ${toolbarSearchWidth - toolbarSearchHeight}px)`,
            zIndex: 1,
          }}
        >
          <Box sx={{ width: toolbarSearchWidth, flexShrink: 0 }}>{searchField}</Box>
        </Box>
      ) : null}
      {desktopSearchRendered ? null : (
        <IconButton
          aria-label="Expand global search"
          onClick={() => {
            setDesktopSearchExpanded(true);
          }}
          sx={{
            width: toolbarSearchHeight,
            height: toolbarSearchHeight,
            color: (theme) =>
              isLightTheme(theme) ? 'rgba(31, 45, 65, 0.64)' : 'rgba(244, 247, 251, 0.72)',
            '& .MuiSvgIcon-root': {
              width: `${toolbarSearchButtonIconSize}px`,
              height: `${toolbarSearchButtonIconSize}px`,
              stroke: 'currentColor',
              strokeWidth: 0.08,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <SearchIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      {isCompact ? (
        <IconButton
          aria-label="Open global search"
          onClick={(event) => {
            openSearch(event.currentTarget);
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      ) : (
        desktopSearchField
      )}
      <Popover
        open={open}
        anchorEl={anchorElement}
        onClose={closeSearch}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        minWidth={isCompact ? 'min(88vw, 390px)' : 420}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        paperSx={{ mt: s('xs'), maxHeight: 'min(70vh, 520px)', overflowY: 'auto' }}
      >
        <Box id="global-search-results" role="listbox" sx={{ p: s('sm') }}>
          {isCompact ? <Box sx={{ mb: s('sm') }}>{searchField}</Box> : null}
          {resultGroups.map((group) => (
            <Box key={group.source} sx={{ '& + &': { mt: s('sm') } }}>
              <Text
                role="label"
                component="div"
                sx={{ color: 'text.secondary', px: s('xs'), pb: s('xs') }}
              >
                {resultGroupLabels[group.source]}
              </Text>
              <Stack gap="xxs">
                {group.results.map((result) => {
                  const resultIndex = visibleResults.findIndex((item) => item.id === result.id);
                  const selected = resultIndex === activeIndex;
                  return (
                    <Box
                      key={result.id}
                      role="option"
                      aria-selected={selected}
                      tabIndex={-1}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => {
                        handleSelectResult(result);
                      }}
                      sx={(theme) => ({
                        cursor: 'pointer',
                        px: s('sm'),
                        py: s('xs'),
                        borderRadius: '6px',
                        backgroundColor: selected ? theme.app.colors.surfaceAlt : 'transparent',
                        '&:hover': {
                          backgroundColor: theme.app.colors.surfaceAlt,
                        },
                      })}
                    >
                      <Text
                        role="bodyStrong"
                        component="div"
                        sx={{ fontWeight: typographyTokens.bodyStrong.fontWeight }}
                      >
                        {result.title}
                      </Text>
                      {result.subtitle ? (
                        <Text role="caption" component="div" sx={{ color: 'text.secondary' }}>
                          {result.subtitle}
                        </Text>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {showLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), p: s('sm') }}>
              <CircularProgress size={16} />
              <Text role="caption">Searching</Text>
            </Box>
          ) : null}
          {entityQuery.isError ? (
            <Box sx={{ p: s('sm') }}>
              <Text role="caption" sx={{ color: 'error.main' }}>
                Search failed
              </Text>
            </Box>
          ) : null}
          {showEmpty ? (
            <Box sx={{ p: s('sm') }}>
              <Text role="caption" sx={{ color: 'text.secondary' }}>
                No results
              </Text>
            </Box>
          ) : null}
        </Box>
      </Popover>
    </>
  );
};
